"use client"

import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import { AlertCircle, Check, CheckCircle2, Copy } from "lucide-react"
import { useState } from "react"

export default function DenunciasPage() {
  const [descricao, setDescricao] = useState("")
  const [email, setEmail] = useState("")
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState("")
  const [success, setSuccess] = useState("")
  const [codigo, setCodigo] = useState("")
  const [copied, setCopied] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setError("")
    setSuccess("")
    setCodigo("")
    setCopied(false)

    try {
      const response = await fetch("/api/denuncias", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ descricao, email }),
      })

      const data = await response.json()

      if (!response.ok) {
        throw new Error(data.message || data.error || "Erro ao enviar denúncia")
      }

      setSuccess("Sua denúncia foi registrada com sucesso!")
      setCodigo(data.codigo)
      setDescricao("")
      setEmail("")
    } catch (err) {
      console.error("Erro detalhado:", err)
      setError(err instanceof Error ? err.message : "Erro ao enviar denúncia")
    } finally {
      setLoading(false)
    }
  }

  const copyToClipboard = async () => {
    try {
      if (navigator.clipboard && window.isSecureContext) {
        // Use the Clipboard API if available
        await navigator.clipboard.writeText(codigo)
      } else {
        // Fallback for older browsers
        const textArea = document.createElement('textarea')
        textArea.value = codigo
        textArea.style.position = 'fixed'
        textArea.style.left = '0'
        textArea.style.top = '0'
        textArea.style.opacity = '0'
        document.body.appendChild(textArea)
        textArea.focus()
        textArea.select()
        document.execCommand('copy')
        document.body.removeChild(textArea)
      }
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch (err) {
      console.error("Erro ao copiar código:", err)
    }
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="max-w-4xl mx-auto">
        <Card>
          <CardHeader>
            <CardTitle>Nova Denúncia</CardTitle>
            <CardDescription>
              Descreva detalhadamente a irregularidade que você deseja denunciar.
              Lembre-se que sua identidade será mantida em sigilo absoluto.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-4">
                <div className="space-y-2">
                  <Textarea
                    id="descricao"
                    placeholder="Descreva a irregularidade..."
                    value={descricao}
                    onChange={(e) => setDescricao(e.target.value)}
                    required
                    className="min-h-[200px]"
                  />
                </div>

                <div className="space-y-2">
                  <Input
                    id="email"
                    type="email"
                    placeholder="Email (opcional) - para receber atualizações"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                  <p className="text-xs text-muted-foreground">
                    Se fornecido, você receberá um email com o código de rastreamento.
                  </p>
                </div>
              </div>

              {error && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertTitle>Erro</AlertTitle>
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              {success && (
                <Alert className="bg-green-50 border-green-200">
                  <CheckCircle2 className="h-4 w-4 text-green-600" />
                  <AlertTitle className="text-green-800">Sucesso!</AlertTitle>
                  <AlertDescription className="text-green-700">
                    <p className="mb-2">{success}</p>
                    <div className="mt-2 p-3 bg-green-100 rounded-md">
                      <p className="text-sm font-medium text-green-800 mb-1">Seu código de acompanhamento:</p>
                      <div className="flex items-center gap-2">
                        <code className="text-lg font-bold text-green-900">{codigo}</code>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          onClick={copyToClipboard}
                          className="text-green-600 hover:text-green-700 relative"
                        >
                          {copied ? (
                            <Check className="h-4 w-4" />
                          ) : (
                            <Copy className="h-4 w-4" />
                          )}
                          {copied && (
                            <span className="absolute -top-8 left-1/2 transform -translate-x-1/2 bg-green-600 text-white text-xs px-2 py-1 rounded whitespace-nowrap">
                              Código copiado!
                            </span>
                          )}
                        </Button>
                      </div>
                      <p className="text-xs text-green-600 mt-2">
                        Guarde este código em local seguro. Ele é necessário para acompanhar sua denúncia.
                      </p>
                    </div>
                  </AlertDescription>
                </Alert>
              )}

              <Button type="submit" disabled={loading}>
                {loading ? "Enviando..." : "Enviar Denúncia"}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  )
} 